import { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { useFeatureFlag } from './useFeatureFlags';
import { useRbac } from '../contexts/RbacContext';
import { WebSocketContext } from '../contexts/WebSocketContext';
import { WebSocketEventType, type MaintenanceModePayload } from '../types/websocket.types';
import type { WebSocketMessage } from '../types/websocket.types';

const HEALTH_CHECK_INTERVAL = 30_000; // 30 секунд
const HEALTH_CHECK_TIMEOUT = 10_000; // 10 секунд

/**
 * Хук для определения режима технических работ.
 *
 * Источники (приоритет):
 * 1. WebSocket event MAINTENANCE_MODE (мгновенное переключение от админа)
 * 2. Feature flag `maintenance_mode` (загружается при старте)
 * 3. Health check polling (fallback при недоступном бэкенде / 500 ошибках)
 *
 * Админы (hasPermission('admin', 'manage')) видят приложение как обычно.
 */
export function useMaintenanceMode() {
  const { isEnabled: flagEnabled, isLoading } = useFeatureFlag('maintenance_mode');
  const { hasPermission } = useRbac();

  // Безопасно читаем WebSocket контекст — не кидаем ошибку если контекст ещё не готов
  const wsContext = useContext(WebSocketContext);
  const wsService = wsContext?.service ?? null;
  const wsStatus = wsContext?.status ?? 'DISCONNECTED';

  const isAdmin = hasPermission('admin', 'manage');

  // WS-override: если пришло событие, используем его значение вместо флага
  const [wsOverride, setWsOverride] = useState<boolean | null>(null);

  // Бэкенд недоступен (health check failed)
  const [backendDown, setBackendDown] = useState(false);

  // Подписка на MAINTENANCE_MODE событие через WebSocket
  const handleMaintenanceEvent = useCallback((payload: unknown) => {
    const data = payload as MaintenanceModePayload;
    setWsOverride(data.enabled);
    // Если maintenance выключили — бэкенд точно жив
    if (!data.enabled) {
      setBackendDown(false);
    }
  }, []);

  useEffect(() => {
    if (!wsService) return;

    const unsubscribe = wsService.on(
      WebSocketEventType.MAINTENANCE_MODE,
      handleMaintenanceEvent as (payload: unknown, message: WebSocketMessage<unknown>) => void,
    );

    return unsubscribe;
  }, [wsService, handleMaintenanceEvent]);

  // Health check polling — запускается когда WebSocket отключён
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const wsDisconnected = wsStatus === 'DISCONNECTED' || wsStatus === 'ERROR';

    if (!wsDisconnected) {
      // WS подключён — очищаем polling и сбрасываем backendDown
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setBackendDown(false);
      return;
    }

    // WS отключён — начинаем polling health
    const checkHealth = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

        const response = await fetch(`${baseUrl}/health`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          setBackendDown(true);
          return;
        }

        const data = await response.json();
        if (data?.data?.maintenance) {
          setBackendDown(true);
        } else {
          setBackendDown(false);
        }
      } catch {
        // Сетевая ошибка / таймаут — бэкенд недоступен
        setBackendDown(true);
      }
    };

    // Проверяем сразу и потом каждые 30 секунд
    checkHealth();
    intervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [wsStatus]);

  const enabled = wsOverride ?? flagEnabled ?? backendDown;

  return {
    isMaintenanceMode: enabled && !isAdmin,
    isLoading,
  };
}
